import { GetGuideQuery } from "graphql/queries/get-guide.generated";
import { GetGuidesQuery } from "graphql/queries/get-guides.generated";
import { INSPECTION_TYPE_OPTIONS } from "constants/constants";
import { BUILDING_TYPE_OPTIONS } from "constants/property-field-options";

/**
 * Checks if a value is considered empty.
 *
 * @param {*} value - The value to check
 * @returns {boolean} True if the value is considered empty, false otherwise
 */
export const isEmpty = value => {
	return value === null || value === undefined || value === "";
};

/**
 * Recursively removes "empty" or "junk" values from an object or array.
 * * This method is more robust than a simple null check as it:
 * 1. Removes null, undefined, and empty strings.
 * 2. Prunes common "junk" string values like "n/a", "null", or "unknown" (case-insensitive).
 * 3. Recursively cleans nested objects and arrays.
 * 4. Removes empty objects {} or arrays [] that result from the cleaning process.
 * 5. Preserves Date instances and other non-object primitives (booleans, numbers).
 * * @param {T} input - The data structure to be sanitized.
 * @returns {T} A new version of the input with all empty/junk values removed.
 * * @example
 * const rawInput = {
 * name: "  ",
 * role: "N/A",
 * tags: ["React", "", null, "unknown"],
 * settings: {
 * theme: null,
 * backups: []
 * }
 * };
 * * const cleanInput = pruneEmptyValues(rawInput);
 * // Output: { tags: ["React"] }
 */
export function pruneEmptyValues<T>(input: T): T {
	const clean = (value: any): any => {
		// Remove null / undefined
		if (value == null) return undefined;

		// String cleanup: trim and discard empty or "junk" terms
		if (typeof value === "string") {
			const s = value.trim();
			const lowered = s.toLowerCase();
			if (s === "" || lowered === "null" || lowered === "n/a" || lowered === "na" || lowered === "unknown") {
				return undefined;
			}
			return value; // Keep original value (no trim) to avoid unintended data modification
		}

		// Keep Date objects as-is
		if (value instanceof Date) return value;

		// Array cleanup: clean items and filter out undefined or empty structures
		if (Array.isArray(value)) {
			const arr = value
				.map(v => clean(v))
				.filter(v => v !== undefined)
				.filter(v => {
					if (Array.isArray(v)) return v.length > 0;
					if (typeof v === "object" && v !== null && !(v instanceof Date)) return Object.keys(v).length > 0;
					return true;
				});

			return arr.length > 0 ? arr : undefined;
		}

		// Object cleanup: clean properties and drop the object if it becomes empty
		if (typeof value === "object") {
			const out: Record<string, any> = {};
			for (const [k, v] of Object.entries(value)) {
				const c = clean(v);
				if (c !== undefined) out[k] = c;
			}
			return Object.keys(out).length > 0 ? out : undefined;
		}

		// Numbers and booleans (including 0 and false) are kept
		return value;
	};

	const result = clean(input);

	// Ensure return type matches input if the result is fully pruned
	return (result === undefined ? (Array.isArray(input) ? ([] as any) : ({} as any)) : result) as T;
}

export const createdAtLabel = (createdAt: string) => {
	const createdAtTime = new Date(createdAt).getTime();
	const now = new Date().getTime();
	const diff = now - createdAtTime;

	const minutes = Math.floor(diff / (1000 * 60));
	const hours = Math.floor(diff / (1000 * 60 * 60));
	const days = Math.floor(diff / (1000 * 60 * 60 * 24));
	const weeks = Math.floor(diff / (1000 * 60 * 60 * 24 * 7));

	let timeAgo = "Just now";
	if (minutes < 60) {
		timeAgo = "Just now";
	} else if (hours < 24) {
		timeAgo = `${hours}h`;
	} else if (days < 7) {
		timeAgo = `${days}d`;
	} else if (weeks < 8) {
		timeAgo = `${weeks}w`;
	} else {
		timeAgo = "8w";
	}

	return timeAgo;
};

export const formatDate = (dateString?: string | null): string => {
	if (!dateString) return "";
	return new Date(dateString).toLocaleString("en-US", {
		month: "numeric",
		day: "numeric",
		year: "2-digit",
		hour: "numeric",
		minute: "2-digit",
		hour12: true
	});
};

export interface ExistingVsActualComparison {
	// Entities that need to be removed from the database
	toRemove: any[];
	// Entities that need to be added to the database
	toAdd: any[];
}

export const compareEntities = (
	currentEntities: any[] = [],
	selectedEntities: any[] = []
): ExistingVsActualComparison => {
	// Entities to remove: exist in currentEntities but not in selectedEntities
	const toRemove = currentEntities.filter(
		currentProp => !selectedEntities.some(selectedProp => selectedProp.id === currentProp.id)
	);

	// Entities to add: exist in selectedEntities but not in currentEntities
	const toAdd = selectedEntities.filter(
		selectedProp => !currentEntities.some(currentProp => currentProp.id === selectedProp.id)
	);

	return {
		toRemove,
		toAdd
	};
};

/*
 * Creates a label for filters based on the first selection and the length of the selection.
 * If there is a first selection, it will be used as the label, and if there are more selections,
 * it will append the count of additional selections.
 *
 * @param {string} defaultLabel - The default label to use if no selections are made.
 * @param {Array} list - The list of selected items.
 * @param {string} customFirstItemName - Changes the display name of the first item used in the label.
 * @returns {string} The label for filters.
 */
export const createLabelForFilters = (defaultLabel: string, list: Array<any>, customFirstItemName?: string) => {
	const listLength = list.length;
	let label = defaultLabel;

	if (listLength) {
		label = customFirstItemName || list[0];

		if (listLength > 1) {
			label += ` + ${listLength - 1} other${listLength > 2 ? "s" : ""}`;
		}
	}

	return label;
};

// O(n) time
export const splitDistinctTypeAndMake = arr => {
	const typeSet = new Map();
	const makeSet = new Set<string>();

	arr.forEach(item => {
		if (!typeSet.get(item.product_category.name)) {
			typeSet.set(item.product_category.name, item.product_category.label);
		}
		if (!makeSet.has(item.make)) {
			makeSet.add(item.make);
		}
	});

	return { distinctTypesSet: typeSet, distinctMakes: [...makeSet] };
};

// A no-op function originally used as a default onChange handler
export const voidFnc = () => undefined;

export const getGuideProductPhotos = (guide: GetGuidesQuery["guides"][number] | GetGuideQuery["guides_by_pk"]) => {
	const photoLinks: string[] = [];

	guide!.product_guides.forEach(pg => {
		const product = pg.product;
		if (!product) return;

		// 1. Try to get the product photo_link directly
		if (product.photo_link) {
			photoLinks.push(product.photo_link);
			return; // We already found one for this product, move on to the next one
		}

		// 2. If there is no photo_link on the product, look in installed_products
		if (product.installed_products && product.installed_products.length > 0) {
			const installedPhoto = product.installed_products.find(ip => ip.photo_link)?.photo_link;

			if (installedPhoto) {
				photoLinks.push(installedPhoto);
				return;
			}
		}

		// 3. If there is no photo_link in installed_products, look in product_attachments
		if (product.product_attachments && product.product_attachments.length > 0) {
			const attachmentPhoto = product.product_attachments.find(pa => pa.photo_link)?.photo_link;

			if (attachmentPhoto) {
				photoLinks.push(attachmentPhoto);
			}
		}
	});

	return photoLinks;
};

// Function to calculate reading time based on word count
export const guideReadTimeCalculator = (items: Array<{ title?: string | null; body?: string | null }>) => {
	const totalWords = items.reduce((wordCount, item) => {
		const titleWords = item.title ? item.title.trim().split(/\s+/).length : 0;
		const bodyWords = item.body ? item.body.trim().split(/\s+/).length : 0;
		return wordCount + titleWords + bodyWords;
	}, 0);

	return Math.ceil(totalWords / 250);
};

// Function to count distinct property_ids across all installed_products in product_guides
export const getDistinctProperties = (guide: GetGuideQuery["guides_by_pk"]) => {
	const distinctProperties = new Set<string>();

	guide!.product_guides.forEach(productGuide => {
		if (productGuide.product?.installed_products) {
			productGuide.product.installed_products.forEach(installedProduct => {
				if (installedProduct.property) {
					distinctProperties.add(installedProduct.property.name);
				}
			});
		}
	});

	return distinctProperties;
};

export const getPropertyAddress = (
	address_string?: string | null,
	standardized_address?: {
		street_1: string;
		street_2?: string | null;
		city: string;
		state: string;
		postal_code: string;
	} | null
): string => {
	if (standardized_address) {
		return standardized_address.street_2
			? `${standardized_address.street_1} ${standardized_address.street_2}, ${standardized_address.city}, ${standardized_address.state} ${standardized_address.postal_code}`
			: `${standardized_address.street_1}, ${standardized_address.city}, ${standardized_address.state} ${standardized_address.postal_code}`;
	}
	return address_string || "";
};

/**
 * Display label for the value stored on inspection_jobs.inspection_type.
 * Values created before an option was renamed (or by the API) fall back to the
 * stored value with separators turned into spaces.
 */
export const getInspectionTypeLabel = (inspectionType?: string | null): string => {
	if (!inspectionType) return "";

	const option = INSPECTION_TYPE_OPTIONS.find(({ value }) => value === inspectionType);
	if (option) return option.label;

	return inspectionType.replace(/[_-]/g, " ").replace(/\b\w/g, character => character.toUpperCase());
};

export const getBuildingTypeLabel = (buildingType?: string | null): string => {
	if (!buildingType) return " - ";

	const option = BUILDING_TYPE_OPTIONS.find(({ value }) => value === buildingType);
	if (option) return option.label;

	return buildingType.replace(/[_-]/g, " ").replace(/\b\w/g, character => character.toUpperCase());
};

/**
 * Converts an ISO 8601 timestamp to the EEN History deeplink format.
 * e.g. "2026-03-25T12:28:32.341+00:00" → "20260325122832.341"
 */
export const formatEenTimestamp = (ts: string): string => {
	const withoutTz = ts.replace(/([+-]\d{2}:\d{2}|Z)$/, "");
	return withoutTz.replace(/[^\d.]/g, "");
};
